import {
  STAGING,
  PRODUCTION,
  CHAT_HANDOFF_SUCCESS,
  HANDOFF_BUTTON_STEP_OUTPUT,
} from './consts';
import { postMessage } from '../../utils/postMessage';

import {
  ContextVariables,
  HandoffConfirmation,
  InitIntentInterface,
  WidgetComponent,
} from 'api/types';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import { useTheme } from '@mui/material/styles';
import tinycolor from 'tinycolor2';
import { AppDispatch } from 'store';

const sendResizeiframeMessage = (isOpen: boolean): void => {
  if (isOpen) {
    // Fixes cases when the widget doesn't expand
    setTimeout(() => {
      postMessage({ event: 'forethoughtWidgetOpened' });
    });
  } else postMessage({ event: 'forethoughtWidgetClosed' });
};

const getLogRocketKey = (): string | null => {
  const REACT_APP_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'local';
  const REACT_APP_LOGROCKET_KEY = process.env.REACT_APP_LOGROCKET_KEY;

  if (REACT_APP_ENVIRONMENT === STAGING) {
    return `${REACT_APP_LOGROCKET_KEY}/stagingsolve-widget`;
  } else if (REACT_APP_ENVIRONMENT === PRODUCTION) {
    return `${REACT_APP_LOGROCKET_KEY}/prodsolve-widget`;
  } else {
    return null;
  }
};

const handleIntentWorkflowTrackingEvents = (
  contextVariables: ContextVariables,
  widgetComponents: WidgetComponent[],
  dispatch: AppDispatch,
  additionalParams: {
    intent_id?: string;
    workflow_id?: string;
  },
) => {
  const chatHandoffStatus = contextVariables[CHAT_HANDOFF_SUCCESS];
  const handoffButtonStepResult = contextVariables[
    HANDOFF_BUTTON_STEP_OUTPUT
  ] as HandoffConfirmation;

  const isPositiveHandoffButtonStepToTrack =
    handoffButtonStepResult === 'yes_resolve';
  const isNegativeHandoffButtonStepToTrack =
    handoffButtonStepResult === 'no_resolve';
  const isPreviousStepArticleStep =
    widgetComponents.length > 1 &&
    widgetComponents[widgetComponents.length - 2]?.component_type ===
      'article_suggestion';

  if (chatHandoffStatus === true) {
    dispatch(
      sendTrackingEvent({
        event_type: 'widget-customer-agent-chat',
        ...additionalParams,
      }),
    );
  } else if (isPositiveHandoffButtonStepToTrack && isPreviousStepArticleStep) {
    dispatch(
      sendTrackingEvent({
        event_type: 'widget-customer-article-suggestion-helped',
        ...additionalParams,
      }),
    );
  } else if (isNegativeHandoffButtonStepToTrack && isPreviousStepArticleStep) {
    dispatch(
      sendTrackingEvent({
        event_type: 'widget-customer-article-suggestion-unhelpful',
        ...additionalParams,
      }),
    );
  }
};

const handleInitIntentWorkflowTrackingEvents = ({
  additionalParams,
  dispatch,
  intent,
  isProactive,
}: {
  additionalParams: {
    intent_id?: string;
    workflow_id?: string;
  };
  dispatch: AppDispatch;
  intent: InitIntentInterface;
  isProactive: boolean;
}) => {
  if (intent?.free_form_query) {
    dispatch(
      sendTrackingEvent({
        event_type: 'widget-customer-ask-question',
      }),
    );
  } else {
    dispatch(
      sendTrackingEvent({
        event_type: isProactive
          ? 'widget-customer-entered-intent-by-proactive-bubble'
          : 'widget-customer-entered-intent-by-button',
        ...additionalParams,
      }),
    );
  }
};

const useBorderColor = () => {
  const theme = useTheme();

  return new tinycolor(theme.palette.colors.black).setAlpha(0.1).toString();
};

export {
  getLogRocketKey,
  sendResizeiframeMessage,
  handleIntentWorkflowTrackingEvents,
  handleInitIntentWorkflowTrackingEvents,
  useBorderColor,
};
